import { dtpPath, useDtpParams } from '../../lib/clinicSite';
import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ArrowRight } from 'lucide-react';
import type { DtpProductResponse } from '@bloom/shared';
import { CategoryIcon } from '../../components/dtp/educational/CategoryIcon';
import { EducationalShell } from '../../components/dtp/educational/EducationalShell';
import { useEducationalCatalog, getFamilyDisplayName } from '../../components/dtp/educational/useEducationalCatalog';
import { HeroIllustration } from '../../components/dtp/educational/HeroIllustration';
import { trackDtpEvent } from '../../lib/dtpAnalytics';

const CATEGORY_HERO_TINTS: Record<string, string> = {
  'weight-loss': 'from-[#e8f0e9] via-[#a8c4ac] to-[var(--store-accent,#2d4a3a)]',
  'oral-ed': 'from-[#ede3d8] via-[#b89876] to-[#6b4a26]',
  'sexual-health': 'from-[#fbe8e8] via-[#d4a0a0] to-[#8b3c3c]',
  'dermatology': 'from-[#f5ebd8] via-[#d4a574] to-[#8b6332]',
  'peptides': 'from-[#efe8f5] via-[#b8a8c8] to-[#5a4a6a]',
  'peptides-healing': 'from-[#fbe8e8] via-[#d4a0a0] to-[#8b3c3c]',
  'peptides-growth-hormone': 'from-[#e3eef5] via-[#8fb3c8] to-[#3a5a78]',
  'peptides-metabolism': 'from-[#f5ebd8] via-[#d4a574] to-[#8b6332]',
  'peptides-collagen': 'from-[#e8f0e9] via-[#a8c4ac] to-[var(--store-accent,#2d4a3a)]',
  'daily-vitamins': 'from-[#e3eef5] via-[#8fb3c8] to-[#3a5a78]',
};

const CATEGORY_TINTS: Record<string, string> = {
  'weight-loss': 'bg-[#eaf2ec] text-[var(--store-accent,#2d4a3a)]',
  'oral-ed': 'bg-[#ede3d8] text-[#6b4a26]',
  'sexual-health': 'bg-[#fbe8e8] text-[#8b3c3c]',
  'dermatology': 'bg-[#f5ebd8] text-[#8b6332]',
  'peptides': 'bg-[#efe8f5] text-[#5a4a6a]',
  'peptides-healing': 'bg-[#fbe8e8] text-[#8b3c3c]',
  'peptides-growth-hormone': 'bg-[#e3eef5] text-[#3a5a78]',
  'peptides-metabolism': 'bg-[#f5ebd8] text-[#8b6332]',
  'peptides-collagen': 'bg-[#eaf2ec] text-[var(--store-accent,#2d4a3a)]',
  'daily-vitamins': 'bg-[#e3eef5] text-[#3a5a78]',
};

export function DtpLearnCategory() {
  const { categorySlug } = useDtpParams<{ categorySlug: string }>();
  const navigate = useNavigate();
  const { loading, error, clinic, products, categoryContent, physicianId } = useEducationalCatalog();
  const lastTrackedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!loading && clinic && categorySlug && lastTrackedRef.current !== categorySlug) {
      lastTrackedRef.current = categorySlug;
      trackDtpEvent({
        physicianId: clinic.physicianId,
        variant: clinic.storefrontVariant,
        eventType: 'category_view',
        categorySlug,
      });
    }
  }, [loading, clinic, categorySlug]);

  const category = useMemo(
    () => categoryContent.find(c => c.slug === categorySlug),
    [categoryContent, categorySlug]
  );

  const subCategories = useMemo(
    () => categoryContent.filter(c => c.parentSlug === categorySlug),
    [categoryContent, categorySlug]
  );

  // Drug families directly under this category (not its sub-cats)
  const drugFamilies = useMemo(() => {
    const productsInCat = products.filter(p => p.primaryCategorySlug === categorySlug);
    const bySlug = new Map<string, DtpProductResponse[]>();
    for (const p of productsInCat) {
      if (!p.drugFamilySlug) continue;
      const list = bySlug.get(p.drugFamilySlug) || [];
      list.push(p);
      bySlug.set(p.drugFamilySlug, list);
    }
    return Array.from(bySlug.entries()).map(([slug, skus]) => {
      const minPrice = Math.min(...skus.map(s => s.price));
      const sample = skus[0];
      return {
        slug,
        displayName: getFamilyDisplayName(slug, products),
        shortPitch: sample.educationalContent?.shortPitch,
        skuCount: skus.length,
        minPrice,
      };
    }).sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [products, categorySlug]);

  // For sub-cats, count drug families per sub-cat
  const subCategoriesWithCounts = useMemo(() => {
    return subCategories.map(sc => {
      const familySet = new Set<string>();
      for (const p of products) {
        if (p.primaryCategorySlug === sc.slug && p.drugFamilySlug) familySet.add(p.drugFamilySlug);
      }
      return { cat: sc, familyCount: familySet.size };
    }).filter(x => x.familyCount > 0)
      .sort((a, b) => (a.cat.order ?? 0) - (b.cat.order ?? 0));
  }, [subCategories, products]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--store-bg,#faf8f4)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--store-accent,#2d4a3a)]" />
      </div>
    );
  }

  if (error || !clinic) {
    return (
      <div className="min-h-screen bg-[var(--store-bg,#faf8f4)] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <h1 className="font-serif text-2xl text-[var(--store-text,#1a1612)]">{error || 'Store unavailable'}</h1>
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <EducationalShell clinic={clinic}>
        <div className="max-w-2xl mx-auto px-5 md:px-8 py-20 text-center">
          <h1 className="font-serif text-3xl text-[var(--store-text,#1a1612)]">Category not found</h1>
          <p className="text-[var(--store-muted,#8a8175)] mt-2">This treatment area isn't available in this store.</p>
          <button
            onClick={() => navigate(dtpPath(physicianId, `/learn`))}
            className="mt-6 inline-flex items-center gap-2 text-[var(--store-accent,#2d4a3a)] underline"
          >Back to shop</button>
        </div>
      </EducationalShell>
    );
  }

  const heroTint = CATEGORY_HERO_TINTS[category.slug] || 'from-[#ede3d8] via-[#b89876] to-[#6b4a26]';
  const isParent = subCategoriesWithCounts.length > 0;
  const splitTitle = (category.heroTitle || category.displayName).split('\n');

  return (
    <EducationalShell clinic={clinic}>
      <div className="bg-[var(--store-bg,#faf8f4)]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-3 md:py-4 text-xs text-[var(--store-muted,#8a8175)] uppercase tracking-wider">
          <button onClick={() => navigate(dtpPath(physicianId, `/learn`))} className="hover:text-[var(--store-text,#1a1612)]">Shop</button>
          <span className="mx-2">/</span>
          {category.parentSlug && (
            <>
              <button onClick={() => navigate(dtpPath(physicianId, `/learn/${category.parentSlug}`))} className="hover:text-[var(--store-text,#1a1612)]">
                {categoryContent.find(c => c.slug === category.parentSlug)?.displayName || category.parentSlug}
              </button>
              <span className="mx-2">/</span>
            </>
          )}
          <span className="text-[var(--store-text,#1a1612)]">{category.displayName}</span>
        </div>
      </div>

      <section className="bg-[var(--store-bg,#faf8f4)]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-12 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-start">
          <div className="md:col-span-7">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[var(--store-accent,#2d4a3a)] mb-4 md:mb-5">
              <span className="w-4 h-4"><CategoryIcon iconKey={category.iconKey} /></span>
              {category.heroEyebrow || category.displayName}
            </div>
            <h1 className="font-serif text-[2.25rem] sm:text-[2.75rem] md:text-[3.5rem] lg:text-[4.25rem] font-medium leading-[1] tracking-tight">
              {splitTitle.length > 1 ? (
                <>
                  {splitTitle[0]}<br/><em className="font-normal">{splitTitle.slice(1).join(' ')}</em>
                </>
              ) : (
                category.displayName
              )}
            </h1>
            {category.heroIntro && (
              <p className="mt-6 md:mt-8 text-base md:text-lg text-[var(--store-muted,#4a4238)] leading-relaxed max-w-xl">
                {category.heroIntro}
              </p>
            )}
          </div>
          <div className="md:col-span-5">
            <div className={`relative aspect-[4/5] rounded-3xl bg-gradient-to-br ${heroTint} shadow-[0_30px_60px_-20px_rgba(26,22,18,0.25)] overflow-hidden`}>
              <HeroIllustration slug={category.slug} />
            </div>
          </div>
        </div>
      </section>

      {category.longExplainer && category.longExplainer.length > 0 && (
        <section className="bg-white border-t border-[var(--store-border,#e8e2d6)]">
          <div className="max-w-2xl mx-auto px-5 md:px-8 py-14 md:py-20">
            {category.longExplainer.map((block, idx) => (
              <div key={idx} className="mb-8">
                <h3 className="font-serif text-xl md:text-2xl font-medium tracking-tight mb-2">{block.heading}</h3>
                <p className="text-[var(--store-muted,#4a4238)] leading-[1.75]">{block.body}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="bg-[var(--store-bg,#faf8f4)] border-t border-[var(--store-border,#e8e2d6)]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-14 md:py-20">
          <div className="mb-10 md:mb-12">
            <div className="text-[11px] md:text-xs uppercase tracking-[0.2em] text-[var(--store-muted,#8a8175)] mb-3">Available in this store</div>
            <h2 className="font-serif text-3xl md:text-4xl font-medium tracking-tight">
              {isParent ? `Four areas ${clinic.name} treats` : `${clinic.name}'s ${category.displayName.toLowerCase()} formulary`}
            </h2>
          </div>

          {isParent ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
              {subCategoriesWithCounts.map(({ cat, familyCount }) => (
                <button
                  key={cat.slug}
                  onClick={() => navigate(dtpPath(physicianId, `/learn/${cat.slug}`))}
                  className="bg-white rounded-2xl p-6 md:p-8 border border-[var(--store-border,#e8e2d6)] text-left transition-transform hover:-translate-y-0.5"
                >
                  <div className={`relative aspect-[16/9] rounded-xl mb-5 md:mb-6 flex items-center justify-center overflow-hidden ${CATEGORY_TINTS[cat.slug] || 'bg-[#ede3d8] text-[#6b4a26]'}`}>
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.5),transparent_55%)]" />
                    <div className="absolute w-[36%] aspect-square rounded-full bg-current opacity-[0.08]" />
                    <div className="relative w-[28%] max-w-[72px] aspect-square">
                      <CategoryIcon iconKey={cat.iconKey} />
                    </div>
                  </div>
                  <h3 className="font-serif text-[22px] md:text-[24px] font-medium">{cat.displayName}</h3>
                  {cat.shortPitch && (
                    <p className="text-[14px] md:text-[15px] text-[var(--store-muted,#4a4238)] mt-3 leading-relaxed">{cat.shortPitch}</p>
                  )}
                  <div className="mt-4 md:mt-5 text-xs text-[var(--store-muted,#8a8175)]">{familyCount} {familyCount === 1 ? 'option' : 'options'}</div>
                </button>
              ))}
            </div>
          ) : drugFamilies.length === 0 ? (
            <div className="text-center py-12 text-[var(--store-muted,#8a8175)]">No medications in this category yet.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
              {drugFamilies.map(fam => (
                <button
                  key={fam.slug}
                  onClick={() => navigate(dtpPath(physicianId, `/learn/${categorySlug}/${fam.slug}`))}
                  className="bg-white rounded-2xl p-6 md:p-8 border border-[var(--store-border,#e8e2d6)] text-left transition-transform hover:-translate-y-0.5"
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <span className="text-[10px] uppercase tracking-[0.06em] font-medium px-2.5 py-1 rounded-full bg-[#e8f0e9] text-[var(--store-accent,#2d4a3a)]">
                      {category.displayName}
                    </span>
                    <span className="text-xs text-[var(--store-muted,#8a8175)] flex-shrink-0">from ${Math.round(fam.minPrice)}</span>
                  </div>
                  <h3 className="font-serif text-[22px] md:text-[26px] font-medium">{fam.displayName}</h3>
                  {fam.shortPitch && (
                    <p className="text-[14px] md:text-[15px] text-[var(--store-muted,#4a4238)] mt-2 leading-relaxed">{fam.shortPitch}</p>
                  )}
                  <div className="mt-5 md:mt-6 flex items-center justify-between pt-4 md:pt-5 border-t border-[var(--store-border,#e8e2d6)]">
                    <span className="text-xs text-[var(--store-muted,#8a8175)]">{fam.skuCount} {fam.skuCount === 1 ? 'option' : 'options'}</span>
                    <span className="text-sm font-medium text-[var(--store-text,#1a1612)] flex items-center gap-1">
                      Learn more <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>
    </EducationalShell>
  );
}

export default DtpLearnCategory;
